// Phase 6 — Angular HttpClient port of the AngularJS `GradeTemplate` factory
// (was sudo/grade-templates/GradeTemplate.js, DELETED). Downgraded back to the AngularJS
// `GradeTemplate` name (see downgrades.ts), so all the still-AngularJS consumers — the edit
// page (EditGradeTemplateController) and the copy/component modal controllers — keep using
// this single service. Every method returns a Promise (via firstValueFrom) so the existing
// `.then(...)` call sites keep working unchanged.
//
// Resolve semantics are preserved EXACTLY from the old factory: every method did
// `$http.get/post(...).then(resp => resp.data)`, i.e. it resolved to the parsed response
// BODY. HttpClient already returns the parsed body, so each method just returns
// firstValueFrom(...) — no `.then(() => true)` special-casing (unlike AccessService). The
// copy/component modals depend on the returned object shape (e.g. `response.status === 'ok'`),
// so this fidelity matters. The `secret-key` header is added globally by secretKeyInterceptor.
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class GradeTemplateService {
    private http = inject(HttpClient);

    list(): Promise<any> {
        return firstValueFrom(this.http.get('/grade-template/list'));
    }

    one(gradeTemplateId: string): Promise<any> {
        return firstValueFrom(this.http.get('/grade-template/one/' + gradeTemplateId));
    }

    create(gradeTemplateObject: any): Promise<any> {
        return firstValueFrom(this.http.post('/grade-template/create', gradeTemplateObject));
    }

    update(gradeTemplateObject: any): Promise<any> {
        return firstValueFrom(this.http.post('/grade-template/update', gradeTemplateObject));
    }

    copy(templateId: string, params: any): Promise<any> {
        return firstValueFrom(
            this.http.post('/grade-template/copy/' + templateId, {
                name: params.name,
                code: params.code,
            }),
        );
    }

    // No-body POST — the legacy `$http.post(url)` sent no body; pass `null` explicitly.
    remove(templateId: string): Promise<any> {
        return firstValueFrom(this.http.post('/grade-template/remove/' + templateId, null));
    }

    toggle(params: any): Promise<any> {
        return firstValueFrom(
            this.http.post('/grade-template/toggle', {
                gradeComponent: params.gradeComponent,
            }),
        );
    }

    addComponent(params: any): Promise<any> {
        return firstValueFrom(
            this.http.post('/grade-template/component/add', {
                gradeTemplate: params.gradeTemplate,
                parent: params.parent,
                replace: params.replace,
                relative: params.relative,
                minValue: params.minValue,
                closesSelection: params.closesSelection,
                mustComplete: params.mustComplete,
                absolute: params.absolute,
                type: params.type,
                name: params.name,
                right: params.right,
                index: params.index,
                startWeekNumber: params.startWeekNumber,
                durationWeeks: params.durationWeeks,
            }),
        );
    }

    editComponent(params: any): Promise<any> {
        return firstValueFrom(
            this.http.post('/grade-template/component/edit', {
                _id: params._id,
                relative: params.relative,
                absolute: params.absolute,
                minValue: params.minValue,
                closesSelection: params.closesSelection,
                mustComplete: params.mustComplete,
                name: params.name,
                type: params.type,
                replace: params.replace,
                right: params.right,
                index: params.index,
                startWeekNumber: params.startWeekNumber,
                durationWeeks: params.durationWeeks,
            }),
        );
    }

    removeComponent(params: any): Promise<any> {
        return firstValueFrom(
            this.http.post('/grade-template/component/remove', {
                _id: params._id,
            }),
        );
    }
}
